import { useContext, useEffect, useState } from "react";
import { AppConfigContext } from "../../../context/AppConfigContext";
import { CartDrawerPreview } from "../CartDrawer/CartDrawer";
import styles from "./CartDrawerAdmin.module.css";
import { MockCartData } from "../cartMockData";
import { Badge, Banner, Divider, Icon, Link } from "@shopify/polaris";
import { fetchStoreProducts, getPixelsForCart } from "../../Bundles/constants";
import useCartDrawerHook from "./useCartDrawerHook";
import { CartConfigInterface } from "../CartTypes";
import CartSettingsCard from "./CartFormCards/CartSettingsCard";
import CartMetaBar from "./CartFormCards/CartMetaBar";
import { SaveBar } from "@shopify/app-bridge-react";
import CartDesignCard from "./CartFormCards/CartDesignCard";
import CartUpsellsForm from "./CartFormCards/CartUpsellsForm";
import CartAnnouncementForm from "./CartFormCards/CartAnnouncementForm";
import CartPositionsCard from "./CartFormCards/CartPositionsCard";
import CartDrawerAdminSkeleton from "./CartDrawerAdminSkeleton";
import { endPoints, getData, postData } from "../../../utilities/networkCalls";
import { ChangeEvents } from "../../Bundles/Types";
import useCartFormFields from "./useCartFormFields";
import { mergeConfig } from "../../utilities";
import { HideIcon } from "@shopify/polaris-icons";
import CartDiscountForm from "./CartFormCards/CartDiscountForm";
import CartAdditionalNoteForm from "./CartFormCards/CartAdditionalNoteForm";
import CartAdditionalProductForm from "./CartFormCards/CartAdditionalProductForm";
import CartRewardsForm from "./CartFormCards/CartRewardsForm";
import TriggeredRewardsForm from "./CartFormCards/TriggeredRewardsForm";
import EnableExtensionModal from "../../../components/EnableExtensionModal";
import ActivateSnippetModal from "../../../components/ActivateSnippetModal/ActivateSnippetModal";
import DesignTabIcon from "../CartIcons/DesignTabIcon";
import PositionTabIcon from "../CartIcons/PositionTabIcon";
import RewardsTabIcon from "../CartIcons/RewardsTabIcon";
import UpsellTabIcon from "../CartIcons/UpsellTabIcon";
import AnnouncementTabIcon from "../CartIcons/AnnouncementTabIcon";
import DiscountsTabIcon from "../CartIcons/DiscountsTabIcon";
import AdditionalNotesIcon from "../CartIcons/AdditionalNotesIcon";
import AdditionalProductsIcon from "../CartIcons/AdditionalProductsIcon";
import SettingsTabIcon from "../CartIcons/SettingsTabIcon";
import RenderError from "../../../components/RenderError/RenderError";
import { useNavigate } from "react-router-dom";
import { isSlideCartEnabledPlan, PLANS } from "../../../components/Plans/plansConstants";
import LimitReachedPopup from "../../../components/Plans/LimitReachedPopup";
import CartAdditionalMessageForm from "./CartFormCards/CartAdditionalMessageForm";
import AdditionalMessageIcon from "../CartIcons/AdditionalMessageIcon";
import useSlideCartMixpanelHook from "../../../hooks/useSlideCartMixpanelHook";
import useIsMobile from "../../../hooks/useIsMobile";
import ToggleSwitch from "../../../components/ToggleSwitch/ToggleSwitch";
import TrustBadgesIcon from "../CartIcons/TrustBadgesIcon";
import TrustBadgesForm from "./CartFormCards/TrustBadgesForm";
import { scrollToTop } from "../../../utilities/functions";
import MetalBirdForm from "./CartFormCards/MetalBirdForm";
import { IS_METAL_BIRD_SHOP } from "../../constants";

const getMetalBirdLabel = () => {
    switch (shopify?.config?.shop) {
        case "2bb5e5-83.myshopify.com":
            return "Mymetalrescue Config";
        case "passionfruitshop.myshopify.com":
            return "Passionfruit Config";
        case "gitpush-1a6si6mt.myshopify.com":
            return "Passionfruit Config";
        default:
            return "Metal Bird Config";
    }
};

const TabSections = [
    {
        label: "Cart Appearance",
        options: [
            { label: "Design", value: "design", isFeat: false, icon: <DesignTabIcon color="#000000" /> },
            { label: "Positions", value: "positions", isFeat: false, icon: <PositionTabIcon color="#000000" /> },
        ],
    },
    {
        label: "Cart Features",
        options: [
            {
                label: "Triggered Rewards",
                value: "triggered_rewards",
                isFeat: true,
                icon: <RewardsTabIcon color="#000000" />,
            },
            { label: "Rewards", value: "reward", isFeat: true, icon: <RewardsTabIcon color="#000000" /> },
            { label: "Upsells", value: "upsell", isFeat: true, icon: <UpsellTabIcon color="#000000" /> },
            {
                label: "Announcements",
                value: "announcement",
                isFeat: true,
                icon: <AnnouncementTabIcon color="#000000" />,
            },
            { label: "Discount Codes", value: "discount", isFeat: true, icon: <DiscountsTabIcon color="#000000" /> },
            {
                label: "Additional Notes",
                value: "additional_note",
                isFeat: true,
                icon: <AdditionalNotesIcon color="#000000" />,
            },
            {
                label: "Additional Products",
                value: "additional_product",
                isFeat: true,
                icon: <AdditionalProductsIcon color="#000000" />,
            },
            {
                label: "Additional Message",
                value: "additional_message",
                isFeat: true,
                icon: <AdditionalMessageIcon color="#000000" />,
            },
            {
                label: "Trust Badges",
                value: "trust_badges",
                isFeat: true,
                icon: <TrustBadgesIcon color="#000000" />,
            },
            ...(IS_METAL_BIRD_SHOP
                ? [
                      {
                          label: getMetalBirdLabel(),
                          value: "metal_bird_config",
                          isFeat: true,
                          icon: <RewardsTabIcon color="#000000" />,
                      },
                  ]
                : []),
        ],
    },
];

const isFeatureActive = (cc: CartConfigInterface, at: any) => {
    let status = false;
    switch (at.value) {
        case "triggered_rewards":
            status = cc?.triggered_rewards?.status;
            break;
        case "reward":
            status = cc?.reward.status;
            break;
        case "upsell":
            status = cc?.upsell?.upsell_status;
            break;
        case "announcement":
            status = cc?.announcement?.announcement_status;
            break;
        case "discount":
            status = cc?.discount?.status;
            break;
        case "additional_note":
            status = cc?.additional_note?.status;
            break;
        case "additional_product":
            status = cc?.additional_product?.status;
            break;
        case "additional_message":
            status = cc?.additional_message?.status;
            break;
        case "trust_badges":
            status = cc?.trust_badges?.status;
            break;
        case "metal_bird_config":
            status = cc?.metal_bird_config?.status as boolean;
            break;
    }
    return status;
};

const TabsOptions = ({
    active,
    setActive,
    dirty,
    cartConfig,
    trackCartFeature = () => {},
    setFeatureActive = () => {},
}: {
    active: any;
    setActive: any;
    dirty: boolean;
    cartConfig: CartConfigInterface;
    trackCartFeature: any;
    setFeatureActive: any;
}) => {
    return (
        <div className={styles.tabsSection}>
            {TabSections.map((section) => (
                <div key={section.label} style={{ marginBottom: 8 }}>
                    <div className={styles.tabsLabel}>{section.label}</div>
                    {section.options.map((option) => (
                        <div
                            onClick={() => {
                                if (dirty) {
                                    shopify.saveBar.leaveConfirmation();
                                } else {
                                    setActive(option);
                                    trackCartFeature({
                                        cta: "Change Tab",
                                        ctaType: "Side Menu",
                                        featName: option.label,
                                    });
                                }
                            }}
                            className={`${styles.tabsOption} ${active.value == option.value && styles.active}`}
                            key={option.value}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                }}
                            >
                                {option.isFeat && (
                                    <span
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                            minWidth: 27,
                                        }}
                                    >
                                        <ToggleSwitch
                                            checked={!!isFeatureActive(cartConfig, option)}
                                            setToggle={(e: any) => {
                                                e?.preventDefault();
                                                e.stopPropagation();
                                                if (dirty) {
                                                    shopify.saveBar.leaveConfirmation();
                                                } else {
                                                    trackCartFeature({
                                                        cta: isFeatureActive(cartConfig, option)
                                                            ? "Deactivate"
                                                            : "Activate",
                                                        ctaType: "Activate Cart Feature",
                                                        featName: option.label,
                                                    });
                                                    setFeatureActive(isFeatureActive(cartConfig, option), option.value);
                                                }
                                            }}
                                            disabled={false}
                                            zoom={0.75}
                                        />
                                    </span>
                                )}
                                {option.icon}
                                {option.label}
                            </div>
                            {option.isFeat && (
                                <span
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "min-content",
                                        visibility: isFeatureActive(cartConfig, option) ? "hidden" : "visible",
                                        height: 18,
                                    }}
                                >
                                    <Icon source={HideIcon} tone="base" />
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ))}
            <div className={styles.dividerStyle}>
                <Divider />
            </div>
            <div
                onClick={() => {
                    if (dirty) {
                        shopify.saveBar.leaveConfirmation();
                    } else {
                        setActive({ label: "Settings", value: "settings", isFeat: false });
                        trackCartFeature({
                            cta: "Change Tab",
                            ctaType: "Side Menu",
                            featName: "Settings",
                        });
                    }
                }}
                style={{ marginTop: 8 }}
                className={`${styles.tabsOption} ${active == "settings" && styles.active}`}
                key={"settings"}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "9px",
                        padding: "0 4px",
                    }}
                >
                    <SettingsTabIcon color="#000000" />
                    {"Settings"}
                </div>
            </div>
        </div>
    );
};

const CartDrawerAdmin = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { plansInfo, sft } = appConfigContext;
    const { trackCartFeature, trackCartTopBar } = useSlideCartMixpanelHook();
    const { isExhausted, currentPlan, currentOrders, isTrial, loadingPlans, slideCartLegacy } = plansInfo;
    const slideCartLocked = !loadingPlans && slideCartLegacy != 1 && !isSlideCartEnabledPlan(currentPlan?.plan_id);
    const goTo = useNavigate();
    const [activeTab, setActiveTab] = useState<{ label: string; value: string; isFeat: boolean }>(
        TabSections[0]?.options[0],
    );
    const [isActivatingCart, setIsActivatingCart] = useState(false);
    const [showCartPreview, setShowCartPreview] = useState(true);
    const { cartConfig, dispatchCartConfig } = useCartDrawerHook();
    const [_config, setConfig] = useState<CartConfigInterface>(cartConfig);
    const [updatingCart, setUpdatingCart] = useState(false);
    const [loadingCartConfig, setLoadingCartConfig] = useState(true);
    const [showCartEmpty, setShowCartEmpty] = useState(false);
    const [showLimitPopup, setShowLimitPopup] = useState(false);
    const { isMobile } = useIsMobile();
    const [storeProducts, setStoreProducts] = useState<any>({ ...MockCartData });
    const { fields, submit, reset, makeClean, dirty, validate, submitErrors } = useCartFormFields({
        _config,
        setConfig,
    });
    const [showEnableCoreSnippetModal, setShowEnableCoreSnippetModal] = useState<boolean>(false);

    useEffect(() => {
        if (!currentPlan || Object.keys(currentPlan).length === 0) {
            goTo("/billing");
        }
    }, [currentPlan]);

    useEffect(() => {
        fetchCartConfig();
    }, []);

    const fetchCartConfig = async () => {
        const res = await getData({ url: endPoints.getCartConfig });
        if (res.status == 200) {
            const _c = mergeConfig(cartConfig, res?.data?.cartConfig);
            setConfig({ ..._c });
            dispatchCartConfig({
                init: {
                    ..._c,
                },
                type: ChangeEvents.initState,
            });
            try {
                const initialData = await fetchStoreProducts(sft, false, 1);
                setStoreProducts(() => {
                    const price = +initialData[0]?.variants[0]?.price * 100 || "10000";
                    const qty = 2;
                    const variant = initialData[0]?.variants[0];
                    return {
                        ...MockCartData,
                        total_price: +price * qty * 0.9,
                        original_total_price: +price * qty,
                        items: [
                            {
                                ...MockCartData.items[0],
                                ...initialData[0],
                                final_line_price: +price * qty * 0.9,
                                original_line_price: +price * qty,
                                line_price: +price,
                                original_price: price,
                                quantity: qty,
                                options_with_values: [],
                                image: variant?.image?.originalSrc,
                                line_level_discount_allocations: MockCartData.items[0].line_level_discount_allocations,
                            },
                        ],
                    };
                });
            } catch (error) {
                console.error("Error fetching AI FBT data:", error);
                setStoreProducts({ ...MockCartData });
            }
        } else {
            setConfig(cartConfig);
        }
        setLoadingCartConfig(false);
    };

    const saveCartConfig = async () => {
        if (validate().length) {
            submit();
            scrollToTop();
            return;
        }
        setUpdatingCart(true);
        const payload = { ...cartConfig };
        const res = await postData({
            url: payload?.cart_id ? endPoints.updateCartConfig : endPoints.saveCartConfig,
            payload,
        });
        if (res.status == 200) {
            if (!payload?.cart_id) {
                const updatedCartConfig = {
                    ...cartConfig,
                    cart_id: res?.data?.cart_id || null,
                    additional_product: {
                        ...cartConfig.additional_product,
                        ...(res?.data?.gift_wrapping_id && { gift_wrapping_id: res?.data?.gift_wrapping_id }),
                        ...(res?.data?.shipping_protection_id && {
                            shipping_protection_id: res?.data?.shipping_protection_id,
                        }),
                    },
                };
                setConfig(updatedCartConfig);
                dispatchCartConfig({
                    init: {
                        ...updatedCartConfig,
                    },
                    type: ChangeEvents.initState,
                });
            } else {
                const updatedCartConfig = {
                    ...cartConfig,
                    additional_product: {
                        ...cartConfig.additional_product,
                        ...(res?.data?.gift_wrapping_id && { gift_wrapping_id: res?.data?.gift_wrapping_id }),
                        ...(res?.data?.shipping_protection_id && {
                            shipping_protection_id: res?.data?.shipping_protection_id,
                        }),
                    },
                };
                setConfig(updatedCartConfig);
                dispatchCartConfig({
                    init: {
                        ...updatedCartConfig,
                    },
                    type: ChangeEvents.initState,
                });
            }
            submit();
            makeClean();
            shopify.toast.show(`Cart Drawer ${payload?.cart_id ? "updated" : "created"} successfully`, {
                isError: false,
            });

            shopify.saveBar.hide("cart-save-bar");
        } else {
            shopify.toast.show("Error while saving cart config", { isError: true });
        }
        setUpdatingCart(false);
    };

    const setFeatureActive = (currentStatus: boolean, tabName: string = "") => {
        switch (tabName) {
            case "reward":
                fields.reward?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "reward",
                });
                break;
            case "triggered_rewards":
                fields.triggered_rewards?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "triggered_rewards",
                });
                break;
            case "upsell":
                fields.upsell.upsell_status.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        upsell_status: !currentStatus,
                    },
                    isComplex: true,
                    key: "upsell",
                });
                break;
            case "announcement":
                fields?.announcement?.announcement_status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        announcement_status: !currentStatus,
                    },
                    isComplex: true,
                    key: "announcement",
                });
                break;
            case "discount":
                fields?.discount?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "discount",
                });
                break;
            case "additional_note":
                fields?.additional_note?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "additional_note",
                });
                break;
            case "additional_product":
                fields?.additional_product?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "additional_product",
                });
                break;
            case "additional_message":
                fields?.additional_message?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "additional_message",
                });
                break;
            case "trust_badges":
                fields?.trust_badges?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "trust_badges",
                });
                break;
            case "metal_bird_config":
                fields?.metal_bird_config?.status?.onChange(!currentStatus);
                dispatchCartConfig({
                    payload: {
                        status: !currentStatus,
                    },
                    isComplex: true,
                    key: "metal_bird_config",
                });
                break;
        }
    };

    useEffect(() => {
        console._log("CartDirty--", dirty);
        if (dirty) {
            shopify.saveBar.show("cart-save-bar");
        } else {
            shopify.saveBar.hide("cart-save-bar");
        }
    }, [dirty]);

    const handleDiscard = () => {
        shopify.saveBar.hide("cart-save-bar");
        dispatchCartConfig({
            init: {
                ..._config,
            },
            type: ChangeEvents.initState,
        });
        reset();
    };

    const handleActivateCart = async (currentState: boolean) => {
        if (!currentState && slideCartLocked) return;
        setIsActivatingCart(true);
        if (!currentState) {
            const isExtensionEnabled = await getData({
                url: endPoints.extensionStatus,
            });
            setIsActivatingCart(false);
            if (isExtensionEnabled.data.status == 0) {
                setShowEnableCoreSnippetModal(true);

                return;
            }
            if (isExhausted && !isTrial) {
                reset();
                shopify.saveBar.hide("cart-save-bar");
                setShowLimitPopup(true);

                return;
            }
        }
        if (validate().length) {
            submit();
            setIsActivatingCart(false);
            return;
        }

        setIsActivatingCart(true);
        const payload = { ...cartConfig, cart_status: !currentState };
        const res = await postData({
            url: payload?.cart_id ? endPoints.updateCartConfig : endPoints.saveCartConfig,
            payload,
        });
        if (res.status == 200) {
            fields?.cart_status?.onChange(payload.cart_status);
            dispatchCartConfig({
                payload: payload.cart_status,
                key: "cart_status",
            });
            if (!payload?.cart_id) {
                setConfig({ ...cartConfig, cart_id: res?.data?.cart_id || null, cart_status: payload.cart_status });
                dispatchCartConfig({
                    payload: res?.data?.cart_id,
                    key: "cart_id",
                });
            } else {
                setConfig({ ...cartConfig, cart_status: payload.cart_status });
            }
            submit();
            makeClean();
            shopify.toast.show(`Cart Drawer ${payload.cart_status ? "activated" : "deactivated"} successfully`, {
                isError: false,
            });

            shopify.saveBar.hide("cart-save-bar");
        } else {
            shopify.toast.show("Error while saving cart config", { isError: true });
        }
        setIsActivatingCart(false);
    };

    useEffect(() => {
        // function to check screen width and toggle cart preview
        const handleResize = () => {
            if (window.innerWidth <= 950) {
                setShowCartPreview(false); // hide preview on small screens (≤1080px)
            } else {
                setShowCartPreview(true); // show preview again on large screens (>1080px)
            }
        };

        window.addEventListener("resize", handleResize);

        handleResize();

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <>
            {loadingCartConfig || updatingCart ? (
                <CartDrawerAdminSkeleton />
            ) : (
                <>
                    <div className={styles.cartFormPreviewWrapper}>
                        <div className={`${styles.cartFormWrapper} ${styles.cartFormWrapperWithPreview}`}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "flex-end",
                                    marginBottom: 20,
                                    gap: 20,
                                }}
                            >
                                <CartMetaBar
                                    fields={fields}
                                    handleActivateCart={handleActivateCart}
                                    cartConfig={cartConfig}
                                    showCartPreview={showCartPreview}
                                    setShowCartPreview={setShowCartPreview}
                                    cartStatus={!!cartConfig?.cart_status}
                                    cartPaused={!!cartConfig?.cart_status && cartConfig?.is_paused}
                                    dirty={dirty}
                                    trackCartTopBar={trackCartTopBar}
                                    isActivatingCart={isActivatingCart}
                                    slideCartLocked={slideCartLocked}
                                />
                                {slideCartLocked && (
                                    <Banner
                                        title={`Slide Cart isn't included in the ${currentPlan?.plan_name || "current"} plan`}
                                        tone="warning"
                                        action={{
                                            content: "Upgrade plan",
                                            onAction: () => {
                                                shopify.saveBar.hide("cart-save-bar");
                                                goTo("/billing");
                                            },
                                        }}
                                    >
                                        You can design and preview your cart, but it can't be activated on your store.
                                        Upgrade to higher plan to enable Slide Cart.
                                    </Banner>
                                )}
                                {isExhausted &&
                                    currentPlan != null &&
                                    !isTrial &&
                                    [PLANS.STARTER, PLANS.FREE_NEW, PLANS.NEW_PLAN]?.includes(currentPlan?.plan_id) && (
                                        <Banner title="Action Required" tone="warning">
                                            You've reached your current plan limit, and your cart has been deactivated.
                                            To activate it, please{" "}
                                            <Link
                                                onClick={() => {
                                                    shopify.saveBar.hide("cart-save-bar");

                                                    goTo("/billing");
                                                }}
                                            >
                                                upgrade
                                            </Link>{" "}
                                            your plan.
                                        </Banner>
                                    )}
                                <RenderError submitErrors={submitErrors} />
                                <EnableExtensionModal setCanShowExpiry={null} appType="Cart Drawer" />
                                {showEnableCoreSnippetModal && (
                                    <ActivateSnippetModal
                                        setShowEnableCoreSnippetModal={setShowEnableCoreSnippetModal}
                                        appType={"cart drawer"}
                                        onSnippetEnabled={() => handleActivateCart(!!cartConfig?.cart_status)}
                                    />
                                )}
                            </div>

                            <div
                                className={styles.cartFormPanels}
                                style={{
                                    gridTemplateColumns: showCartPreview
                                        ? "30% calc(70% - 1rem)"
                                        : "20% calc(80% - 1rem)",
                                }}
                            >
                                <div className={styles.tabsPanel}>
                                    <TabsOptions
                                        active={activeTab}
                                        setActive={setActiveTab}
                                        dirty={dirty}
                                        cartConfig={cartConfig}
                                        trackCartFeature={trackCartFeature}
                                        setFeatureActive={setFeatureActive}
                                    />
                                </div>
                                <div className={styles.formPanel}>
                                    <div className={styles.panelTitleWrapper}>
                                        <div className={styles.panelTitle}>
                                            {activeTab.label}
                                            {!!activeTab?.isFeat && (
                                                <Badge
                                                    tone={
                                                        isFeatureActive(cartConfig, activeTab) ? "success" : "warning"
                                                    }
                                                >
                                                    {isFeatureActive(cartConfig, activeTab) ? "Active" : "Disabled"}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    {activeTab.value == "design" && (
                                        <CartDesignCard
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            cartConfig={cartConfig}
                                            showCartPreview={showCartPreview}
                                            setConfig={setConfig}
                                        />
                                    )}
                                    {activeTab.value == "positions" && (
                                        <CartPositionsCard
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                        />
                                    )}
                                    {activeTab.value == "reward" && (
                                        <CartRewardsForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "triggered_rewards" && (
                                        <TriggeredRewardsForm fields={fields} dispatchCartConfig={dispatchCartConfig} />
                                    )}
                                    {activeTab.value == "upsell" && (
                                        <CartUpsellsForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "announcement" && (
                                        <CartAnnouncementForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "discount" && (
                                        <CartDiscountForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "additional_note" && (
                                        <CartAdditionalNoteForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "additional_product" && (
                                        <CartAdditionalProductForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                            sft={sft}
                                        />
                                    )}
                                    {activeTab.value == "additional_message" && (
                                        <CartAdditionalMessageForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "trust_badges" && (
                                        <TrustBadgesForm
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                        />
                                    )}
                                    {activeTab.value == "metal_bird_config" && (
                                        <MetalBirdForm fields={fields} dispatchCartConfig={dispatchCartConfig} />
                                    )}
                                    {activeTab.value == "settings" && (
                                        <CartSettingsCard
                                            fields={fields}
                                            dispatchCartConfig={dispatchCartConfig}
                                            submit={submit}
                                            showCartPreview={showCartPreview}
                                            setShowCartEmpty={setShowCartEmpty}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        {showCartPreview && (
                            <div
                                className={styles.previewPanel}
                                style={{
                                    width: isMobile
                                        ? "100%"
                                        : getPixelsForCart(cartConfig.cart_design.cart_width + "-admin"),
                                    maxWidth: isMobile
                                        ? "100%"
                                        : getPixelsForCart(cartConfig.cart_design.cart_width + "-admin"),
                                }}
                            >
                                <CartDrawerPreview
                                    cartConfig={cartConfig}
                                    showCartDrawer={true}
                                    appConfigContext={appConfigContext}
                                    cartData={{
                                        ...storeProducts,
                                        ...(showCartEmpty && { items: [] }),
                                    }}
                                    setErrors={() => {}}
                                    setShowCartDrawer={setShowCartPreview}
                                    errors={null}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}

            {updatingCart ? (
                <SaveBar id="cart-save-bar">
                    <button variant="primary" loading="" onClick={saveCartConfig} disabled={updatingCart}></button>
                    <button loading=""></button>
                    <button onClick={handleDiscard}></button>
                </SaveBar>
            ) : (
                <SaveBar id="cart-save-bar">
                    <button variant="primary" onClick={saveCartConfig} disabled={updatingCart}></button>
                    <button onClick={handleDiscard}></button>
                </SaveBar>
            )}

            {showLimitPopup && (
                <LimitReachedPopup
                    setPopup={setShowLimitPopup}
                    isExhausted={isExhausted}
                    appType={"cart"}
                    currentOrders={currentOrders}
                    currentPlan={currentPlan}
                />
            )}
        </>
    );
};

export default CartDrawerAdmin;
